import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HostPortalClient, SeatPurchaseInfo, ShareWebinarRecordingRequest } from 'src/app/Services/WOWApi.Service';

export interface ShareWebinarDialogData {
  webinarId: number;
  hostUserId: number;
  seats: SeatPurchaseInfo[];
}

export interface ShareRecipient {
  userId: number;
  name: string;
  email: string;
  selected: boolean;
}

/**
 * Lets a host pick from the people who actually purchased a seat in this webinar and
 * email them the rewatch link. Recipients shown here are only a suggestion - the API
 * re-derives real seat purchasers server-side and never trusts these ids blindly.
 */
@Component({
    selector: 'app-share-webinar-dialog',
    templateUrl: './share-webinar-dialog.component.html',
    styleUrls: ['./share-webinar-dialog.component.css'],
    standalone: false
})
export class ShareWebinarDialogComponent implements OnInit {
  recipients: ShareRecipient[] = [];
  sending = false;
  errorMessage: string = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ShareWebinarDialogData,
    private dialogRef: MatDialogRef<ShareWebinarDialogComponent>,
    private hostPortalClient: HostPortalClient
  ) {}

  ngOnInit(): void {
    const byUser = new Map<number, ShareRecipient>();
    (this.data.seats || []).forEach(seat => {
      if (!seat.userId) {
        return;
      }
      if (!byUser.has(seat.userId)) {
        byUser.set(seat.userId, {
          userId: seat.userId,
          name: seat.seatOwner || 'Unknown User',
          email: seat.email,
          selected: false
        });
      }
    });
    this.recipients = Array.from(byUser.values());
  }

  get allSelected(): boolean {
    return this.recipients.length > 0 && this.recipients.every(r => r.selected);
  }

  get someSelected(): boolean {
    return this.recipients.some(r => r.selected);
  }

  toggleAll(checked: boolean): void {
    this.recipients.forEach(r => r.selected = checked);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onShare(): void {
    const recipientUserIds = this.recipients.filter(r => r.selected).map(r => r.userId);
    if (recipientUserIds.length === 0) {
      return;
    }

    const request = new ShareWebinarRecordingRequest();
    request.webinarId = this.data.webinarId;
    request.recipientUserIds = recipientUserIds;

    this.sending = true;
    this.errorMessage = null;
    this.hostPortalClient.shareWebinarRecording(this.data.hostUserId, request).subscribe({
      next: (sentCount) => {
        this.sending = false;
        this.dialogRef.close(sentCount);
      },
      error: () => {
        this.sending = false;
        this.errorMessage = 'Something went wrong sending the rewatch link. Please try again.';
      }
    });
  }
}
